import type { Command, CommandResult } from "@minga/core"
import { ProjectRpcs } from "@minga/protocol"
import { makeDurableRpcClient } from "@minga/protocol/durable-rpc-client"
import { Clock, Duration, Effect, Layer, type Schedule, type Scope, Stream } from "effect"
import type { RpcClient, RpcClientError } from "effect/rpc"
import { Socket } from "effect/socket"
import { jitteredBackoff, sleepJittered } from "./backoff"
import type { ProjectStore } from "./store"

/**
 * The project sync client (spec §7.6 step 2, W1). One socket per open project:
 *   Subscribe{fromSeq: store.resumeFrom()} → frames into the store; on any end of the subscription (socket closed,
 *   deploy, `Resync`, a gap) resubscribe with the store's current position. Dispatch sends one command and settles it.
 * The client is any `ProjectRpcs` client: the DurableRpc socket client in the browser, `RpcTest` in tests.
 */

export type ProjectClient = RpcClient.FromGroup<typeof ProjectRpcs, RpcClientError.RpcClientError>

/** Ends a subscription so the loop resubscribes from scratch (Resync frame or a seq gap). */
class ResyncRequested {
  readonly _tag = "ResyncRequested"
}

export interface SyncOptions {
  /** Wait between resubscribes after a failure. Default 0.5 s, doubling up to 15 s, with jitter. */
  readonly retry?: Schedule.Schedule<unknown, unknown>
  /** A subscription that stood this long without failing counts as live. Default 1.5 s. */
  readonly settleAfter?: Duration.Input
  /** Pause before resubscribing after a subscription ended normally. Default 250 ms. */
  readonly pause?: Duration.Input
  /**
   * Decision 0151: awaited once before the first Subscribe (the socket opens meanwhile): the state fetched over HTTP,
   * so the first Subscribe asks only for what came after it. Must not fail.
   */
  readonly before?: Effect.Effect<void>
}

const defaultRetry: Schedule.Schedule<unknown, unknown> = jitteredBackoff("500 millis", 15_000)

/** One subscription: frames go into the store; ends with ResyncRequested when the store asks for a snapshot. */
const subscribeOnce = (client: ProjectClient, store: ProjectStore, settleAfter: Duration.Input) =>
  Stream.unwrap(
    Effect.sync(() => {
      // decision 0151: a state fetched over HTTP before the first socket is not a reconnect
      store.setStatus(store.liveEpoch() > 0 ? "reconnecting" : "connecting")
      return client.Subscribe({ fromSeq: store.resumeFrom() })
    }),
  ).pipe(
    Stream.map((frame): Tick => ({ frame })),
    // A caught-up resubscribe gets no frame at all: call it live once it has stood for a moment without failing.
    Stream.merge(
      Stream.fromEffect(Effect.sleep(settleAfter)).pipe(Stream.map((): Tick => ({ frame: null }))),
    ),
    Stream.mapEffect((tick) => {
      if (tick.frame === null) {
        if (store.getSnapshot().confirmed) store.setStatus("live")
        return Effect.void
      }
      return store.applyFrame(tick.frame) === "resync" ? Effect.fail(new ResyncRequested()) : Effect.void
    }),
  )

type Tick = { readonly frame: Parameters<ProjectStore["applyFrame"]>[0] | null }

/**
 * Runs forever: subscribe, apply frames, resubscribe on any end. Interrupt it to close the project.
 * `Stream.retry` resubscribes after an error (socket ended, resync); a subscription that completes normally is
 * resubscribed too, since the server only ends one on its own when the socket goes away.
 */
export const runSync = (client: ProjectClient, store: ProjectStore, options: SyncOptions = {}) => {
  const loop = syncLoop(client, store, options)
  return options.before ? Effect.andThen(options.before, loop) : loop
}

const syncLoop = (client: ProjectClient, store: ProjectStore, options: SyncOptions) =>
  subscribeOnce(client, store, options.settleAfter ?? "1500 millis").pipe(
    Stream.retry(options.retry ?? defaultRetry),
    Stream.runDrain,
    // A defect (a frame the store could not fold, a bug) must not end the project's sync for good.
    Effect.catchDefect((defect) => Effect.logError("project sync defect", defect)),
    Effect.andThen(Effect.sleep(options.pause ?? "250 millis")),
    Effect.forever,
  )

export interface DispatchOptions {
  /** Re-send a failed send after about this long (with jitter) even if no reconnect was seen. Default 3 s. */
  readonly retryAfter?: Duration.Input
  /** Stop re-sending after this long and report the command as not delivered. Default 2 min. */
  readonly giveUpAfter?: Duration.Input
}

/** Resolves once the store's connection has become live again since `epoch`. */
const liveAgain = (store: ProjectStore, epoch: number) =>
  Effect.callback<void>((resume) => {
    if (store.liveEpoch() > epoch) return resume(Effect.void)
    const unsubscribe = store.subscribe(() => {
      if (store.liveEpoch() > epoch) {
        unsubscribe()
        resume(Effect.void)
      }
    })
    return Effect.sync(unsubscribe)
  })

/**
 * Send one command: it goes into `pending` at once, then the result settles it.
 *
 * Every Dispatch failure is a transport failure (the rpc has no typed error): the socket closed before the result
 * came back, or the client is reconnecting and refuses to send. The server may have applied the command, so it stays
 * pending (`unconfirmed`) and the SAME envelope (same commandId) is re-sent once the connection is live again; the
 * server's dedupe answers `Deduplicated` if the first send was applied. Only after `giveUpAfter` is it reported as
 * not delivered.
 */
export const dispatch = (
  client: ProjectClient,
  store: ProjectStore,
  command: Command,
  options: DispatchOptions = {},
) =>
  Effect.gen(function* () {
    const envelope = store.propose(command)
    const deadline = (yield* Clock.currentTimeMillis) + Duration.toMillis(options.giveUpAfter ?? "2 minutes")
    const retryAfter = options.retryAfter ?? "3 seconds"
    const send: Effect.Effect<CommandResult, RpcClientError.RpcClientError> = Effect.suspend(() => {
      const epoch = store.liveEpoch()
      return client.Dispatch(envelope).pipe(
        Effect.catch((error) =>
          Effect.gen(function* () {
            if ((yield* Clock.currentTimeMillis) >= deadline) return yield* Effect.fail(error)
            store.unconfirm(envelope.commandId)
            yield* liveAgain(store, epoch).pipe(Effect.race(sleepJittered(retryAfter)))
            return yield* send
          }),
        ),
      )
    })
    return yield* send.pipe(
      Effect.tap((result: CommandResult) => Effect.sync(() => store.settle(envelope.commandId, result))),
      Effect.tapError((error) =>
        Effect.sync(() => store.fail(envelope.commandId, `Not delivered: ${describeError(error)}`)),
      ),
    )
  })

const describeError = (error: unknown): string =>
  typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"
    ? error.message
    : String(error)

/** The W1 URL for a project, from the page's origin (http → ws, https → wss). */
export const projectSocketUrl = (origin: string, workspaceId: string, projectId: string): string =>
  `${origin.replace(/^http/, "ws")}/api/workspaces/${encodeURIComponent(workspaceId)}/projects/${encodeURIComponent(projectId)}/live`

/** The browser's socket layer: same-origin cookie auth, the global WebSocket constructor. */
export const browserSocket = (url: string): Layer.Layer<Socket.Socket> =>
  Socket.layerWebSocket(url).pipe(Layer.provide(Socket.layerWebSocketConstructorGlobal))

/** A DurableRpc `ProjectRpcs` client over a socket layer, built into the caller's scope. */
export const makeProjectClient = (socket: Layer.Layer<Socket.Socket>) =>
  makeDurableRpcClient(ProjectRpcs, socket) as Effect.Effect<ProjectClient, never, Scope.Scope>
